import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { inventoryCounts, type InventoryCountListItem } from '@/api/inventoryCounts';
import { refs } from '@/api/refs';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

/** Стан інвентаризації — людською (зауваження власника 2026-08-11). */
const STATE_LABELS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', '#64748b'],
  IN_PROGRESS: ['в процесі', '#b45309'],
  POSTED: ['виконано', '#15803d'],
  CANCELLED: ['скасовано', '#dc2626'],
};

export default function InventoryCountsPage() {
  const orgNames = useOrgNames();
  const navigate = useNavigate();
  // Стандартні фільтри списків: орг (за шапкою) + статус + період.
  const f = useDocListFilters();
  const [whFilter, setWhFilter] = useState('');
  const warehousesQ = useQuery({
    queryKey: ['warehouses', f.orgId],
    queryFn: () => refs.warehouses(f.orgId),
    enabled: !!f.orgId,
  });

  // 2026-08-11 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<InventoryCountListItem>(
    (limit, offset, sort) => inventoryCounts.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      warehouse_id: whFilter || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [whFilter, ...f.queryKey],
    { attachmentsType: 'doc.inventory_counts' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<InventoryCountListItem>('doc.inventory_counts', f.orgId);

  const whName = useMemo(() => {
    const m = new Map<string, string>();
    warehousesQ.data?.forEach((w) => m.set(w.id, `${w.name} — ${w.code}`));
    return m;
  }, [warehousesQ.data]);

  const cols = useMemo<ColDef<InventoryCountListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: InventoryCountListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата документа', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'inventory_date', headerName: 'Дата інвентаризації', width: 160, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'warehouse_id', headerName: 'Склад', flex: 1, minWidth: 200, sortable: false,
      valueFormatter: (p) => p.value ? (whName.get(p.value) ?? '') : '',
    },
    {
      field: 'state', headerName: 'Стан', width: 130, sortable: true,
      cellRenderer: (p: { data?: InventoryCountListItem }) => {
        const st = p.data?.state;
        if (!st) return '';
        const [label, color] = STATE_LABELS[st] ?? [st, '#334155'];
        return <span style={{ color, fontWeight: st === 'POSTED' ? 600 : 400 }}>{label}</span>;
      },
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [whName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Інвентаризація товарів<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/inventory-counts/new')}>
          + Нова інвентаризація
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Виконані' },
          { value: 'draft', label: 'Не проведені' },
        ]} />
        <select value={whFilter} onChange={(e) => setWhFilter(e.target.value)}
          style={{ width: 200, flex: '0 0 auto' }}>
          <option value="">Усі склади</option>
          {warehousesQ.data?.map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
      </DocListFiltersRow>
      <InfiniteDocGrid<InventoryCountListItem>
        copyRow={(r) => navigate(`/inventory-counts/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => inventoryCounts.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="inventory-counts"
        onRowDoubleClicked={(r) => navigate(`/inventory-counts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
